/**
 * A dropdown whose options come from the connected app, live.
 *
 * This is the whole mechanism behind "pick a Slack channel" / "pick a repo" /
 * "pick an Airtable table". The field's schema carries `x-lookup` naming another
 * action on the same connector; we run it through the connection and render what
 * comes back. There is no connector-specific code here or anywhere else in the
 * frontend.
 *
 * `depends_on` makes dropdowns chain: an Airtable table list depends on the
 * chosen base, so changing the base refetches the tables and clears a now-stale
 * selection.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { actionsApi } from '@/core/api/actions.api'
import { queryKeys } from '@/lib/query.client'
import { formatError } from '@/core/utils/error.formatter'
import type { LookupSpec } from '@/core/types/api.types'
import { FieldShell, TextField, type FieldProps } from './primitives'
import { Spinner } from '@/shared/components'

interface Props extends FieldProps {
  lookup: LookupSpec
  connectionId: string | null
  /** All current form values, so `depends_on` can be resolved. */
  formValues: Record<string, unknown>
  /** Field key -> its human label, so prompts name the field the user sees. */
  siblingLabels?: Record<string, string>
}

export function DynamicSelectField({
  lookup,
  connectionId,
  formValues,
  siblingLabels,
  ...field
}: Props) {
  const { name, value, onChange, error, disabled } = field

  // `x-lookup` comes from hand-written JSON, so treat every optional key as
  // possibly absent. The API normalises these, but a schema is user-authored
  // data and a missing key must degrade, not throw.
  const dependsOn = useMemo(
    () => (Array.isArray(lookup.depends_on) ? lookup.depends_on : []),
    [lookup.depends_on],
  )
  const labelKey = lookup.label_key || 'name'
  const valueKey = lookup.value_key || 'id'

  // Only the declared dependencies, and only when they have a value: sending a
  // half-filled parent would make the lookup fail rather than return nothing.
  const dependencies = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const key of dependsOn) {
      const v = formValues[key]
      if (v !== undefined && v !== null && v !== '') out[key] = v
    }
    return out
  }, [dependsOn, formValues])

  const missingDeps = dependsOn.filter((k) => dependencies[k] === undefined)
  const ready = Boolean(connectionId) && missingDeps.length === 0

  // "Choose Author first" beats "Choose user_id first": the user never saw the
  // schema key, only the label above the field.
  const missingLabels = missingDeps.map((k) => siblingLabels?.[k] ?? k)

  const query = useQuery({
    queryKey: queryKeys.lookup(connectionId ?? '', lookup.action, dependencies),
    enabled: ready,
    // Options are stable for a while and a dropdown opening shouldn't re-hit a
    // rate-limited provider.
    staleTime: 120_000,
    retry: false,
    queryFn: ({ signal }) =>
      actionsApi.lookup(connectionId!, lookup.action, {
        labelKey,
        valueKey,
        dependencies,
        signal,
      }),
  })

  const options = query.data?.options ?? []

  // When a parent changes, a previously chosen value may no longer be offered.
  // Silently keeping it would submit something the user can no longer see.
  const lastDeps = useRef(JSON.stringify(dependencies))
  useEffect(() => {
    const serialized = JSON.stringify(dependencies)
    if (serialized === lastDeps.current) return
    lastDeps.current = serialized
    if (value !== undefined && value !== null && value !== '') onChange(undefined)
    // `value`/`onChange` are intentionally excluded: this must fire on a
    // dependency change, not on every keystroke of its own value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencies])

  const [manual, setManual] = useState(false)

  // A failed lookup must not trap the user: fall back to typing the value.
  // Missing scopes, an unshared Notion database and a revoked token all land
  // here, and all of them still allow a valid id to be entered by hand.
  if (manual || (query.isError && !query.isFetching)) {
    const e = query.error ? formatError(query.error) : null
    return (
      <div className="stack" style={{ gap: 5 }}>
        <TextField {...field} />
        {e && (
          <p className="field-help">
            Could not load options ({e.message}). Enter the value directly, or{' '}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setManual(false)
                query.refetch()
              }}
            >
              retry
            </button>
            .
          </p>
        )}
        {!e && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setManual(false)}
          >
            Choose from a list instead
          </button>
        )}
      </div>
    )
  }

  const selectedIsKnown =
    value === undefined ||
    value === null ||
    value === '' ||
    options.some((o) => String(o.value) === String(value))

  return (
    <FieldShell {...field}>
      <div className="row-tight">
        <select
          id={`field-${name}`}
          className="select"
          disabled={disabled || !ready || query.isLoading}
          aria-invalid={error ? true : undefined}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => {
            const raw = e.target.value
            if (raw === '') return onChange(undefined)
            // Hand back the option's original value so an integer id stays an
            // integer — the backend's schema validation is strict about type.
            const found = options.find((o) => String(o.value) === raw)
            onChange(found ? found.value : raw)
          }}
        >
          <option value="">
            {!connectionId
              ? 'Choose a connection first'
              : missingLabels.length > 0
                ? `Choose ${missingLabels.join(' and ')} first`
                : query.isLoading
                  ? 'Loading options…'
                  : options.length === 0
                    ? 'No options available'
                    : 'Select…'}
          </option>

          {/* Keep an unrecognised current value visible rather than resetting
              the box to blank behind the user's back. */}
          {!selectedIsKnown && <option value={String(value)}>{String(value)} (current)</option>}

          {options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>

        {query.isFetching && <Spinner />}

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          title="Refetch options from the connected app"
          disabled={!ready}
          onClick={() => query.refetch()}
        >
          ↻
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          title="Type the value instead"
          onClick={() => setManual(true)}
        >
          ✎
        </button>
      </div>

      {ready && !query.isFetching && options.length > 0 && (
        <p className="field-help">
          {options.length} option{options.length === 1 ? '' : 's'} loaded live via{' '}
          <span className="mono">{lookup.action}</span>
        </p>
      )}
    </FieldShell>
  )
}
